import { IConditionVariable, IMessage, IProcedure } from './../redux/timeline-data.model';
import { ReplaySubject, Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { IAction, IDataSourceContainer, ICondition, IDataSource } from '../redux/timeline-data.model';
import { IWidget } from '../interfaces/IWidget';
import { ReduxService } from '../../../../services/redux.service';
import { TimelineDataActions } from '../redux/timeline-data-actions';
import { ITimelineDataState } from '../redux/timeline-data.store';
import { Constants } from '../classes/constants';


export enum ConversionType {
	PercentageToPixels = 'percentage to pixels (from redux to interface)',
	PixelsToPercentage = 'pixels to percentage (from interface to redux)'
}

export interface DesignDimensions {
	width: number;
	height: number;
	zoomFactor?: number;
	fromMedia?: boolean;
}

// this Service handles design metrics and conversion of values based on said metrics
@Injectable()
export class InteractivityDesignDimensionsService {
	public eventHandlerNames: string[];

	public maxZoomFactor = 125;
	public designDimensionsSubject: ReplaySubject<DesignDimensions>;


	public designDimensions: DesignDimensions;
	private _zoomStepFactor = 1.25;
	private subscriptions = new Subscription();
	private defaultDesignDimensions: DesignDimensions = {
		width: 1280,
		height: 720,
		zoomFactor: 0.5,
		fromMedia: false
	};

	constructor(
		private reduxService: ReduxService) {
		this.designDimensionsSubject = new ReplaySubject<DesignDimensions>(1);
		this.eventHandlerNames = Constants.eventHandlerNames;
		// defaults
		this.designDimensions = { ...this.defaultDesignDimensions };
		this.designDimensionsSubject.next(this.designDimensions);

		this.subscriptions.add(this.reduxService.stateObs.subscribe((state: ITimelineDataState) => {
			if (state && state.data && state.data.options &&
				(
					(state.data.options.designWidth && this.designDimensions.width !== state.data.options.designWidth) ||
					(state.data.options.designHeight && this.designDimensions.height !== state.data.options.designHeight)
				)
			) {
				this.setDesignDimensions({
					width: state.data.options.designWidth,
					height: state.data.options.designHeight
				}, false, true);
			}
		}));
	}

	public setDesignDimensions(designDimensions: DesignDimensions, initial?: boolean, noRedux = false) {
		this.designDimensions = { ...this.designDimensions, ...designDimensions };
		if (!noRedux) {
			this.setReduxDesignDimensions(designDimensions, initial);
		}
		this.designDimensionsSubject.next(this.designDimensions);
	}

	public zoomIn() {
		const newZoom = this.designDimensions.zoomFactor * this._zoomStepFactor;
		this.setZoomFactor(newZoom);
	}

	public zoomOut() {
		const newZoom = this.designDimensions.zoomFactor / this._zoomStepFactor;
		this.setZoomFactor(newZoom);
	}

	public setZoomFactor(f: number, fromMedia = false) {
		if (f > this.maxZoomFactor) {
			f = this.maxZoomFactor;
		}
		if (this.designDimensions.zoomFactor !== f) {
			this.designDimensions.zoomFactor = f;

			this.designDimensions.zoomFactor = f;
			this.designDimensionsSubject.next({
				width: this.designDimensions.width,
				height: this.designDimensions.height,
				zoomFactor: this.designDimensions.zoomFactor,
				fromMedia
			});
		}
	}

	public setReduxDesignDimensions(designDimensions: DesignDimensions, resetHistory = false) {
		const ra: any = {
			type: TimelineDataActions.SET_DESIGN_DIMENSIONS,
			designWidth: designDimensions.width,
			designHeight: designDimensions.height,
			description: `Setting project dimensions to ${designDimensions.width}x${designDimensions.height}`
		};
		if (resetHistory) {
			ra.history = 'reset';
		}
		this.reduxService.dispatch(ra);
	}

	public get zoomFactor() {
		return this.designDimensions.zoomFactor;
	}

	public convertWidget(widget: IWidget, conversionType: ConversionType): IWidget {
		const defaultStateSet = widget.data.statesets.find((s) => s.name === 'default'); // only default stateSet for now
		widget.interfaceConfig = {};
		// make a flat interface value config for each state
		defaultStateSet.states.forEach((state) => {
			if (state && state.config) {
				widget.interfaceConfig[state.name] = {};

				Object.keys(state.config).forEach((configKey) => {
					if (this.eventHandlerNames.includes(configKey)) {
						// handled below for default config only
					} else {
						// one way only
						if (conversionType === ConversionType.PercentageToPixels) {
							const value = state.config[configKey];
							if (typeof value === 'string') {
								widget.interfaceConfig[state.name][configKey] = this.translateValueForInterface(
									{ key: configKey, val: value });
							} else if (typeof value === 'object' && configKey.startsWith('textFormat') && value?.textFormat) {
								Object.keys(value.textFormat).forEach((key) => {
									const fullKey = 'textFormat/textFormat/' + key;
									widget.interfaceConfig[state.name][fullKey] = this.translateValueForInterface(
										{ key: fullKey, val: value.textFormat[key] });
								});
							}
						}
					}
				});
			}
		});
		if (widget && widget.config) {
			Object.keys(widget.config).forEach((configKey) => {
				if (this.eventHandlerNames.indexOf(configKey) > -1) {
					let ev = widget.config[configKey];
					ev = this.convertEventHandler(ev, conversionType);
				}
			});
		}
		return widget;
	}

	public convertEventHandler(ev: any, conversionType: ConversionType): any {
		if (ev && ev.length) {
			ev.forEach((part: IProcedure) => {
				if (part && part.condition) {
					part.condition = this.convertCondition(part.condition, conversionType);
				}

				if (part && part.actions && part.actions.length) {
					part.actions.forEach((action: IAction) => {
						action = this.convertAction(action, conversionType);
					});
				}

				if (part && part.else_actions && part.else_actions.length) {
					part.else_actions.forEach((action: IAction) => {
						action = this.convertAction(action, conversionType);
					});
				}
			});
		}
		return ev;
	}

	public convertAction(action: IAction, conversionType: ConversionType): IAction {
		if (action && action.parameters && action.parameters.length) {
			this.convertActionParameters(action, conversionType);
		}
		return action;
	}

	public convertCondition(condition: ICondition, conversionType: ConversionType): ICondition {
		if (condition.variables && condition.variables.length) {
			condition.variables.forEach((conditionVariable: IConditionVariable) => {
				if (conditionVariable && conditionVariable.entityType && conditionVariable.rule) {
					if (conditionVariable.entityType && conditionVariable.entityType === 'widget') { // other types are not convertable a.t.m.
						if (conversionType === ConversionType.PixelsToPercentage) {
							conditionVariable.rule.rvalue = this.translateValueForRedux(
								{ key: conditionVariable.rule.lvalue, val: conditionVariable.rule.rvalue as any }
							);
						} else {
							conditionVariable.rule.rvalue = this.translateValueForInterface(
								{ key: conditionVariable.rule.lvalue, val: conditionVariable.rule.rvalue as any }
							);
						}
					}
				}
			});
		}
		return condition;
	}

	public convertDataSource(propertyName: string, dataSource: IDataSource, conversionType: ConversionType): IDataSource {
		if (conversionType === ConversionType.PixelsToPercentage) {
			dataSource.defaultValue = this.translateValueForRedux({ key: propertyName, val: dataSource.defaultValue as any });
		} else {
			dataSource.defaultValue = this.translateValueForInterface({ key: propertyName, val: dataSource.defaultValue as any });
		}
		// no futher values to convert next to the defaultValue a.t.m.
		return dataSource;
	}

	public convertMessages(messages: IMessage[], conversionType: ConversionType): any {
		for (let i = 0; i < messages.length; i++) {
			const message: IMessage = messages[i];
			if (message && message.procedures && message.procedures.length) {
				message.procedures.forEach((procedure: IProcedure) => { });
			}
		}
	}

	public convertForms() {

	}

	public convertAnimationData() {

	}

	public convertActionParameters(action: IAction, conversionType: ConversionType) {
		// only handle widget actions for now
		if (action.entity === 'widget' && action.parameters && action.parameters.length > 1 && action.parameters[1].value) {
			// action.parameters[0] is widget id
			// action.parameters[1] is command on widget
			if (typeof action.parameters[1].value === 'string') {

				const command: string | IDataSourceContainer = action.parameters[1].value;
				let propertyName: string;

				if (typeof command === 'string') {
					if (command === 'customTransition') {
						// parameters for customTransition are set to index 2-5
						if (action.parameters[3] && action.parameters[3].value) {
							action.parameters[3].value = this.convertValue('xpos', action.parameters[3].value, conversionType);
						}
						if (action.parameters[4] && action.parameters[4].value) {
							action.parameters[4].value = this.convertValue('ypos', action.parameters[4].value, conversionType);
						}
						if (action.parameters[5] && action.parameters[5].value) {
							action.parameters[5].value = this.convertValue('width', action.parameters[5].value, conversionType);
						}
						if (action.parameters[6] && action.parameters[6].value) {
							action.parameters[6].value = this.convertValue('height', action.parameters[6].value, conversionType);
						}
					} else {
						if (command.indexOf('setProperty') !== -1) {
							propertyName = command.replace('setProperty:', '');

							for (let i = 2; i < action.parameters.length; i++) {
								const actionParam = action.parameters[i];

								if (actionParam.name === 'p_2') {
									actionParam.value = this.convertValue(propertyName, actionParam.value, conversionType);
								}
							}
						}
					}
				} else {
					// command is datasource - ? : !
				}


			}
		}
	}

	public convertValue(propertyName: string, value: string | IDataSourceContainer, conversionType: ConversionType) {
		if (value && typeof value === 'object' && value.datasource) {
			value.datasource = this.convertDataSource(propertyName, value.datasource, conversionType);
		} else {

			if (conversionType === ConversionType.PixelsToPercentage) {
				value = this.translateValueForRedux({ key: propertyName, val: value as any });
			} else {
				value = this.translateValueForInterface({ key: propertyName, val: value as any });
			}
		}
		return value;
	}


	public translateValueForInterface(prop: any): any {
		let datasource: IDataSource;
		const key = prop.key;
		let val = prop.val;

		if (typeof val === 'object' && val.datasource && typeof val.datasource.defaultValue !== 'undefined') {
			datasource = val;
			val = datasource.defaultValue;
		}
		switch (key) {
			case 'xpos': 								// x position
			case 'width':
			case 'textFormat/textFormat/leftMargin': 	// text margin left // DEPRECATED
			case 'textFormat/textFormat/rightMargin': 	// text margin right // DEPRECATED
				val = (!isNaN(parseFloat(prop.val))) ? (+(this.designDimensions.width * parseFloat(prop.val) / 100).toFixed(2)).toString() : prop.val;
				break;

			case 'ypos': 								// y position
			case 'height':
			case 'cornerradius': 						// border corner radius
			case 'borderthickness': 					// border thickness
			case 'textFormat/textFormat/fontsize': 		// font size
				val = (!isNaN(parseFloat(prop.val))) ? (+(this.designDimensions.height * parseFloat(prop.val) / 100).toFixed(2)).toString() : prop.val;
				break;

			case 'alpha':								// image alpha
			case 'background_alpha': 					// background opacity
			case 'textFormat/textFormat/background_alpha': // background opacity
			case 'toppadding': 							// text padding top // DEPRECATED
			case 'bottompadding': 						// text padding bottom // DEPRECATED
				val = (!isNaN(parseFloat(prop.val))) ? (+(parseFloat(prop.val)).toFixed(2)).toString() : prop.val;
				break;

			case 'textFormat/textFormat/margin': 		// text margin(s)
				val = this.multiPercToMultiNumber(val);
				break;

			case 'padding':
				val = this.multiNumberToMultiNumber(val);
				break;

		}
		if (datasource) {
			datasource.defaultValue = val;
			return datasource;
		} else {
			return val;
		}
	}


	public translateValueForRedux(changedProp: any): any {
		let datasource: IDataSource;
		let val = changedProp.val;
		const key = changedProp.key;
		if (val !== '__...__' && val !== '' && val !== null) {
			if (typeof val === 'object' && val.datasource && typeof val.datasource.defaultValue !== 'undefined') {
				datasource = val;
				val = datasource.defaultValue;
			}
			switch (key) {
				case 'xpos':
				case 'width':
				case 'textFormat/textFormat/leftMargin': // text margin left // DEPRECATED
				case 'textFormat/textFormat/rightMargin': // text margin right // DEPRECATED
					val = (+(100 * parseFloat(val) / this.designDimensions.width).toFixed(4)).toString() + '%'; // NB + removes '0' decimals!
					break;

				case 'ypos':
				case 'height':
				case 'cornerradius':
				case 'borderthickness':
				case 'textFormat/textFormat/fontsize':
					val = (+(100 * parseFloat(val) / this.designDimensions.height).toFixed(4)).toString() + '%'; // NB + removes '0' decimals!
					break;

				case 'textFormat/textFormat/margin':
					val = this.multiNumberToMultiPerc(val);
					// val = this.multiNumberToMultiNumber(val);
					break;

				case 'padding':
					// val = this.multiNumberToMultiPerc(val);
					val = this.multiNumberToMultiNumber(val);
					break;

				case 'toppadding':							// text padding top // DEPRECATED
				case 'bottompadding':						// text padding bottom // DEPRECATED
					val = (+parseFloat(val).toFixed(1)).toString(); // NB + removes '0' decimals!
					break;

				case 'alpha':								// image alpha
				case 'background_alpha': 					// background opacity
				case 'textFormat/textFormat/background_alpha': // background opacity
					val = (+parseFloat(val).toFixed(2)).toString() + '%'; // NB + removes '0' decimals!
					break;
			}
			if (datasource) {
				datasource.defaultValue = val;
				return datasource;
			} else {
				return val;
			}
		} else if (val === '') {
			return val;
		} else {
			return null;
		}
	}

	private multiValueToParts(multiValue: string) {
		const parts: string[] = multiValue ? multiValue.split(';') : [];
		while(parts?.length < 4) {
			const fromIndex = parts.length === 1 ? 0 : (parts.length - 2);
			parts[parts.length] = parts[fromIndex];
		}
		return parts;
	}

	public multiPercToMultiPerc(value: string) {
		const parts = this.multiValueToParts(value);
		for (let i = 0, l = parts.length; i < l; i++) {
			parts[i] = (!isNaN(parseFloat(parts[i]))) ? (+parseFloat(parts[i]).toFixed(3)).toString() + '%' : '0%'; // NB + removes '0' decimals!
		}
		return parts.join(';');
	}

	public multiPercToMultiNumber(value: string) {
		const parts = this.multiValueToParts(value);
		for (let i = 0, l = parts.length; i < l; i++) {
			const designSize = (i % 2) ? this.designDimensions.width : this.designDimensions.height;
			parts[i] = (!isNaN(parseFloat(parts[i]))) ? (+(designSize * parseFloat(parts[i]) / 100).toFixed(1)).toString() : '0'; // NB + !
		}
		return parts.join(';');
	}

	public multiNumberToMultiNumber(value: string) {
		const parts = this.multiValueToParts(value);
		for (let i = 0, l = parts.length; i < l; i++) {
			parts[i] = (!isNaN(parseFloat(parts[i]))) ? (+parseFloat(parts[i]).toFixed(1)).toString() : '0'; // NB + removes '0' decimals!
		}
		return parts.join(';');
	}

	public multiNumberToMultiPerc(value: string) {
		const parts = this.multiValueToParts(value);
		for (let i = 0, l = parts.length; i < l; i++) {
			const designSize = (i % 2) ? this.designDimensions.width : this.designDimensions.height;
			parts[i] = (!isNaN(parseFloat(parts[i]))) ? (+(100 * parseFloat(parts[i]) / designSize).toFixed(3)).toString() + '%' : '0%'; // NB + !
		}
		return parts.join(';');
	}
}
